import { type ReactElement } from 'react';
import { Link, redirect, useActionData, useLoaderData } from 'react-router';
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Alert } from '@marketplace/ui';
import { type PayoutStatus } from '@marketplace/contracts';
import { localeOf } from '../features/auth/actions.js';
import { ContinueWithStripe } from '../features/payouts/ContinueWithStripe.js';
import { PayoutsAccount } from '../features/payouts/PayoutsAccount.js';
import {
  becomeAProPath,
  fromOf,
  isWatched,
  startOnboarding,
  type OnboardingFailure,
} from '../features/payouts/onboarding.js';
import { usePayoutsWatch } from '../features/payouts/watch.js';
import { ApiError } from '../shared/api.js';
import { queryKeys, routeContext } from '../shared/query.js';
import { loadSession } from '../shared/session.js';

/**
 * `/:lang/account/payouts` — where a provider connects a payout account, and sees where it stands.
 * `W5-T01` §4.3.
 *
 * **Two pages on one path.** With `?step=registration` it is the last step of becoming a provider:
 * why we ask, *Connect with Stripe*, and *Skip for now* to the feed. `become-a-pro` lands here after a
 * profile is created. It is a page of its own, so a provider who closes the tab finds it again from
 * `/account/pro`. Without it, it is the account page: the status line, and *Continue with Stripe* when
 * the next step is the provider's.
 *
 * **`?return=<from>` is Stripe's `return_url`.** Coming back does not mean Stripe has everything, so
 * the page shows what the status is, and re-reads it for a minute while waiting can change it.
 *
 * The status is our table's answer, never Stripe's (§4.1). The action asks for a single-use Account
 * Link and redirects to it; nothing keeps it.
 */
type PayoutsData =
  | { locale: string; view: 'step' }
  | { locale: string; view: 'account'; payouts: PayoutStatus; watch: boolean };

interface PayoutsActionData {
  failure: OnboardingFailure;
}

export async function loader({
  params,
  request,
  context,
}: LoaderFunctionArgs): Promise<PayoutsData> {
  const locale = localeOf(params);

  const session = await loadSession(context);
  if (session === null) throw redirect(`/${locale}/login`);

  const search = new URL(request.url).searchParams;
  // The step asks nothing of the API: what it offers does not depend on the status.
  if (search.get('step') === 'registration') return { locale, view: 'step' };

  const { queryClient, api } = context.get(routeContext);
  try {
    // `staleTime: 0`: this page exists to show the latest answer, and the watch re-runs this loader.
    const payouts = await queryClient.fetchQuery({
      queryKey: queryKeys.myPayouts(),
      queryFn: () => api.getMyPayouts(),
      staleTime: 0,
    });
    return { locale, view: 'account', payouts, watch: search.has('return') };
  } catch (error) {
    // `409`: a provider role with no profile; `403`: not a provider. Both are made one at
    // `become-a-pro`, which is where `/account/pro` sends them too.
    if (error instanceof ApiError && (error.status === 409 || error.status === 403)) {
      throw redirect(becomeAProPath(locale));
    }
    throw error;
  }
}

export async function action({
  params,
  request,
  context,
}: ActionFunctionArgs): Promise<PayoutsActionData | Response> {
  const locale = localeOf(params);
  const form = await request.formData();
  return startOnboarding(context, locale, fromOf(form.get('from')));
}

export function Component(): ReactElement {
  const data = useLoaderData<PayoutsData>();
  const failure = useActionData<PayoutsActionData>()?.failure ?? null;

  return data.view === 'step' ? (
    <RegistrationStep locale={data.locale} failure={failure} />
  ) : (
    <AccountPage data={data} failure={failure} />
  );
}

function RegistrationStep({
  locale,
  failure,
}: {
  locale: string;
  failure: OnboardingFailure | null;
}): ReactElement {
  const { t } = useTranslation();

  return (
    <article data-testid="payouts-step">
      <h1>{t('payouts.step.title')}</h1>
      <p className="mp-lead">{t('payouts.step.why')}</p>
      {failure === null ? null : <Alert tone="error">{t(failure)}</Alert>}
      <ContinueWithStripe
        locale={locale}
        from="registration"
        label={t('payouts.action.connect')}
        step
      />
      <p>
        <Link to={`/${locale}/feed`}>{t('payouts.action.skip')}</Link>
      </p>
    </article>
  );
}

function AccountPage({
  data,
  failure,
}: {
  data: Extract<PayoutsData, { view: 'account' }>;
  failure: OnboardingFailure | null;
}): ReactElement {
  usePayoutsWatch(data.watch && isWatched(data.payouts.status));
  return <PayoutsAccount locale={data.locale} payouts={data.payouts} failure={failure} />;
}
